// Kept import-free (like deep-link-nav-guard.ts) so it stays unit-testable in
// vitest's node environment.

/**
 * Which push-notification deep links the app will follow.
 *
 * A notification's `data.deepLink` used to go straight into `router.push`, so
 * whatever string reached a nurse's phone decided where the app went: an
 * external URL, a route with a crafted query, any screen at all. Now it must be
 * one of the app's real screens, in the exact shape the API sends; anything
 * else is ignored and the tap just opens the app (as a push with no deep link
 * always has).
 *
 * The list is derived from both ends and a test keeps it honest
 * (src/__tests__/push-deep-link.test.ts):
 *   - every `deepLink` the API builds today (realtime/notification-catalog.ts,
 *     jobs/workers/send-compliance-alert.ts, compliance-expired-digest.ts,
 *     send-activation-notification.ts, routes/admin-payroll.ts,
 *     correction-bot/tools/toolkit.ts) must pass, and
 *   - every entry must be a screen that exists under app/.
 * A new push destination means one new line here, in the same PR.
 */

/** `:id` in a pattern = a positive integer id, as every id the API sends is. */
export const PUSH_DEEP_LINK_ROUTES: readonly string[] = [
    "/visit/:id", // app/visit/[id]/index.tsx: visit assigned, request accepted, corrections waiting
    "/available-visit/:id", // app/available-visit/[id].tsx: visit offered, wave opened, request declined
    "/tickets", // app/tickets/index.tsx
    "/tickets/:id", // app/tickets/[id].tsx: new support message
    "/onboarding", // app/onboarding/index.tsx: onboarding update, activation
    "/compliance", // app/compliance/index.tsx: compliance alerts, expired digest
    "/compliance/:id", // app/compliance/[docId].tsx: one compliance item
    "/payments", // app/(tabs)/payments.tsx: payment sent
    "/visits", // app/(tabs)/visits.tsx
    "/support" // app/(tabs)/support.tsx
];

const ID_SEGMENT = /^[1-9][0-9]{0,15}$/;
const MAX_LENGTH = 200;

function matches(pattern: string, path: string): boolean {
    const want = pattern.split("/");
    const got = path.split("/");
    if (want.length !== got.length) {
        return false;
    }
    for (let i = 0; i < want.length; i++) {
        if (want[i] === ":id") {
            if (!ID_SEGMENT.test(got[i])) {
                return false;
            }
        } else if (want[i] !== got[i]) {
            return false;
        }
    }
    return true;
}

/**
 * The deep link to follow, or null to ignore it. Only an in-app path on the
 * allowlist passes: no scheme, no host, no query, no fragment, no `..`.
 */
export function safePushDeepLink(raw: unknown): string | null {
    if (typeof raw !== "string" || raw.length === 0 || raw.length > MAX_LENGTH) {
        return null;
    }
    // Rejects "https://...", "//host", "taskhealth://...", "?x", "#x" and
    // whitespace tricks in one go: a real route is "/" + plain segments.
    if (!/^\/[A-Za-z0-9/_-]*$/.test(raw) || raw.startsWith("//")) {
        return null;
    }
    return PUSH_DEEP_LINK_ROUTES.some((p) => matches(p, raw)) ? raw : null;
}
